// src/pages/PlansPage.jsx
import { useState, useEffect } from "react";

function useWindowWidth() {
  const [width, setWidth] = useState(window.innerWidth);
  useEffect(() => {
    const handler = () => setWidth(window.innerWidth);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return width;
}

export default function PlansPage() {
  const windowWidth = useWindowWidth();
  const isMobile = windowWidth < 640;

  const cardStyle = {
    background: "white",
    borderRadius: 12,
    padding: isMobile ? "24px 20px" : "28px 28px",
    boxShadow: "0 1px 4px rgba(0,0,0,0.07)",
    display: "flex",
    flexDirection: "column",
    width: isMobile ? "100%" : undefined,
    flex: isMobile ? undefined : 1,
    minWidth: 0,
    boxSizing: "border-box",
  };

  const featureStyle = {
    fontSize: 13,
    color: "#3a5068",
    lineHeight: 1.7,
    display: "flex",
    alignItems: "flex-start",
    gap: 8,
    marginBottom: 6,
  };

  const checkmark = (available) => (
    <span style={{ color: available ? "#2e7d32" : "#c0bfbd", fontWeight: 700, flexShrink: 0, marginTop: 1 }}>
      {available ? "✓" : "—"}
    </span>
  );

  const Badge = ({ label, color }) => (
    <span style={{
      fontSize: 10, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase",
      padding: "2px 8px", borderRadius: 20,
      background: color === "green" ? "#e6f4ea" : color === "blue" ? "#e3f0fb" : "#f0f0f0",
      color: color === "green" ? "#2e7d32" : color === "blue" ? "#1e5fa8" : "#888",
      marginLeft: 8,
    }}>
      {label}
    </span>
  );

  return (
    <div style={{ maxWidth: 1100, margin: "0 auto", padding: isMobile ? "24px 16px" : "48px 24px" }}>

      {/* Nav */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        background: "#1e3a5f",
        borderRadius: 10,
        padding: isMobile ? "12px 16px" : "14px 24px",
        marginBottom: 32,
      }}>
        <a href="/" style={{ lineHeight: 0 }}>
          <img src="/oskar_india_tango_logo.png" alt="Oskar India Tango" style={{ height: isMobile ? 32 : 40, width: "auto" }} />
        </a>
        <a href="/" style={{ fontSize: 13, color: "rgba(255,255,255,0.7)", textDecoration: "none" }}>
          ← Back to app
        </a>
      </div>

      {/* Header */}
      <div style={{ textAlign: "center", marginBottom: isMobile ? 32 : 48 }}>
        <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", color: "#6b8cae", marginBottom: 10 }}>
          Oskar India Tango — a product of Kloos International BV
        </div>
        <h1 style={{ fontSize: isMobile ? 24 : 32, fontWeight: 700, color: "#1e3a5f", margin: "0 0 12px 0" }}>
          Plans & Pricing
        </h1>
        <p style={{ fontSize: isMobile ? 14 : 15, color: "#6b8cae", margin: "0 auto", maxWidth: 520, lineHeight: 1.6 }}>
          For inspectors and auditors across maritime, offshore and industrial environments. Start free for 14 days — no payment required.
        </p>
      </div>

      {/* Plan cards */}
      <div style={{
        display: "flex",
        flexDirection: isMobile ? "column" : "row",
        gap: 20,
        alignItems: isMobile ? "stretch" : "stretch",
      }}>

        {/* Solo */}
        <div style={{ ...cardStyle, border: "2px solid #1e3a5f" }}>
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: "#1e3a5f", marginBottom: 4, display: "flex", alignItems: "center" }}>
              Solo <Badge label="Available now" color="green" />
            </div>
            <div style={{ fontSize: 28, fontWeight: 700, color: "#1e3a5f" }}>€56<span style={{ fontSize: 14, fontWeight: 400, color: "#6b8cae" }}>/month</span></div>
            <div style={{ fontSize: 12, color: "#6b8cae", marginTop: 2 }}>or €560/year — save €112</div>
            <div style={{ fontSize: 11, color: "#94afc9", marginTop: 2 }}>Prices excl. 21% BTW</div>
          </div>
          <div style={{ flex: 1, marginBottom: 24 }}>
            <div style={featureStyle}>{checkmark(true)} 1 user</div>
            <div style={featureStyle}>{checkmark(true)} Unlimited inspections</div>
            <div style={featureStyle}>{checkmark(true)} PDF reports (interim &amp; final)</div>
            <div style={featureStyle}>{checkmark(true)} Evidence workflow</div>
            <div style={featureStyle}>{checkmark(true)} Open point tracking</div>
            <div style={featureStyle}>{checkmark(true)} 500 MB storage</div>
            <div style={featureStyle}>{checkmark(true)} Photos (compressed)</div>
            <div style={featureStyle}>{checkmark(false)} Word/Excel template import</div>
            <div style={featureStyle}>{checkmark(false)} Custom branding</div>
            <div style={featureStyle}>{checkmark(false)} Client access portal</div>
            <div style={{ fontSize: 12, color: "#6b8cae", marginTop: 8 }}>Media retained 6 months after closure</div>
          </div>
          <a href="/billing" style={{
            display: "block", textAlign: "center", background: "#1e3a5f", color: "white",
            borderRadius: 8, padding: "10px 0", fontSize: 14, fontWeight: 500, textDecoration: "none",
          }}>
            Start free trial
          </a>
        </div>

        {/* Team */}
        <div style={{ ...cardStyle, border: "1px solid #d0dcea", opacity: 0.85 }}>
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: "#1e3a5f", marginBottom: 4, display: "flex", alignItems: "center" }}>
              Team <Badge label="Coming soon" color="blue" />
            </div>
            <div style={{ fontSize: 28, fontWeight: 700, color: "#1e3a5f" }}>€386<span style={{ fontSize: 14, fontWeight: 400, color: "#6b8cae" }}>/month</span></div>
            <div style={{ fontSize: 12, color: "#6b8cae", marginTop: 2 }}>&nbsp;</div>
          </div>
          <div style={{ flex: 1, marginBottom: 24 }}>
            <div style={featureStyle}>{checkmark(true)} Up to 10 users</div>
            <div style={featureStyle}>{checkmark(true)} Everything in Solo</div>
            <div style={featureStyle}>{checkmark(true)} 100 GB storage</div>
            <div style={featureStyle}>{checkmark(true)} Full resolution photos</div>
            <div style={featureStyle}>{checkmark(true)} Word/Excel template import</div>
            <div style={featureStyle}>{checkmark(false)} Custom branding</div>
            <div style={featureStyle}>{checkmark(false)} Client access portal</div>
            <div style={featureStyle}>{checkmark(false)} API access</div>
            <div style={{ fontSize: 12, color: "#6b8cae", marginTop: 8 }}>Media retained 24 months after closure</div>
          </div>
          <a href="mailto:info@oskar-india-tango.app" style={{
            display: "block", textAlign: "center", background: "#e8eef5", color: "#1e3a5f",
            borderRadius: 8, padding: "10px 0", fontSize: 14, fontWeight: 500, textDecoration: "none",
          }}>
            Register interest
          </a>
        </div>

        {/* Business */}
        <div style={{ ...cardStyle, border: "1px solid #d0dcea", opacity: 0.85 }}>
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: "#1e3a5f", marginBottom: 4, display: "flex", alignItems: "center" }}>
              Business <Badge label="Coming soon" color="blue" />
            </div>
            <div style={{ fontSize: 28, fontWeight: 700, color: "#1e3a5f" }}>€1273<span style={{ fontSize: 14, fontWeight: 400, color: "#6b8cae" }}>/month</span></div>
            <div style={{ fontSize: 12, color: "#6b8cae", marginTop: 2 }}>&nbsp;</div>
          </div>
          <div style={{ flex: 1, marginBottom: 24 }}>
            <div style={featureStyle}>{checkmark(true)} Unlimited users</div>
            <div style={featureStyle}>{checkmark(true)} Everything in Team</div>
            <div style={featureStyle}>{checkmark(true)} 1 TB storage</div>
            <div style={featureStyle}>{checkmark(true)} Custom branding</div>
            <div style={featureStyle}>{checkmark(true)} Client access portal</div>
            <div style={featureStyle}>{checkmark(true)} API access</div>
            <div style={featureStyle}>{checkmark(false)} Video upload</div>
            <div style={{ fontSize: 12, color: "#6b8cae", marginTop: 8 }}>Custom media retention</div>
          </div>
          <a href="mailto:info@oskar-india-tango.app" style={{
            display: "block", textAlign: "center", background: "#e8eef5", color: "#1e3a5f",
            borderRadius: 8, padding: "10px 0", fontSize: 14, fontWeight: 500, textDecoration: "none",
          }}>
            Register interest
          </a>
        </div>

        {/* Enterprise */}
        <div style={{ ...cardStyle, border: "1px solid #d0dcea", background: "#f8fafc" }}>
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: "#1e3a5f", marginBottom: 4, display: "flex", alignItems: "center" }}>
              Enterprise <Badge label="Custom" color="grey" />
            </div>
            <div style={{ fontSize: 28, fontWeight: 700, color: "#1e3a5f" }}>Custom</div>
            <div style={{ fontSize: 12, color: "#6b8cae", marginTop: 2 }}>pricing on request</div>
          </div>
          <div style={{ flex: 1, marginBottom: 24 }}>
            <div style={featureStyle}>{checkmark(true)} Unlimited users</div>
            <div style={featureStyle}>{checkmark(true)} Everything in Business</div>
            <div style={featureStyle}>{checkmark(true)} Video upload</div>
            <div style={featureStyle}>{checkmark(true)} Original quality media</div>
            <div style={featureStyle}>{checkmark(true)} Dedicated support</div>
            <div style={featureStyle}>{checkmark(true)} Custom integrations</div>
            <div style={featureStyle}>{checkmark(true)} Negotiated retention</div>
          </div>
          <a href="mailto:info@oskar-india-tango.app" style={{
            display: "block", textAlign: "center", background: "#1e3a5f", color: "white",
            borderRadius: 8, padding: "10px 0", fontSize: 14, fontWeight: 500, textDecoration: "none",
          }}>
            Contact us
          </a>
        </div>

      </div>

      {/* Footer note */}
      <div style={{ textAlign: "center", marginTop: 40, fontSize: 13, color: "#6b8cae", lineHeight: 1.7 }}>
        All plans include a 14-day free trial with full access. No credit card required to start.
        <br />
        Questions? Email <a href="mailto:info@oskar-india-tango.app" style={{ color: "#1e7fc7" }}>info@oskar-india-tango.app</a>
        <br />
        <a href="/terms" style={{ color: "#6b8cae" }}>Terms</a> · <a href="/privacy" style={{ color: "#6b8cae" }}>Privacy</a>
      </div>

    </div>
  );
}